import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ReplaySubject, of } from 'rxjs';
import { take, map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { IUserInfo, IUserInfoResponse } from '../../models/user-info.model';
import { readFromStorage, writeToStorage, removeFromStorage } from '../../../shared/utils/storage.utils';

@Injectable({ providedIn: 'root' })
export class UserInfoService {
  private readonly http = inject(HttpClient);

  private readonly _ready$ = new ReplaySubject<void>(1);
  /** Emits once after load() completes (success, error, or no-hash). */
  readonly ready$ = this._ready$.asObservable();

  readonly userInfo = signal<IUserInfo | null>(this.parseFromStorage());

  load(): void {
    const hash = readFromStorage('x-userinfo-hash');
    if (!hash) {
      this._ready$.next();
      return;
    }

    this.http
      .get<IUserInfoResponse>(`${environment.OAUTH_API}/userinfo/${hash}.json`)
      .pipe(
        take(1),
        map(res => res?.user ?? null),
        catchError(() => of(null)),
      )
      .subscribe(user => {
        if (user) {
          this.userInfo.set(user);
          writeToStorage('userDetail', JSON.stringify(user));
        }
        this._ready$.next();
      });
  }

  /**
   * Drop the cached identity so a subsequent user never inherits stale details.
   * Called on logout — clears both the in-memory signal and the persisted claims.
   */
  clear(): void {
    this.userInfo.set(null);
    removeFromStorage('userDetail');
  }

  private parseFromStorage(): IUserInfo | null {
    const raw = readFromStorage('userDetail');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as IUserInfo;
    } catch {
      return null;
    }
  }
}
